/* ------------------------------------------------------------------------------
 *
 *  # User Management Components
 *
 *  User grid and related functionality using DataTables
 *
 * ---------------------------------------------------------------------------- */

import { 
	initGenericDataTable, 
	refreshDataTable, 
	destroyDataTable 
} from '../integrations/datatables.js';

// User Grid Variables
let userGridCallbacks = null;

// Helper functions to simulate UserInfo methods
function getFullName(user) {
	return `${user.firstName || ''} ${user.lastName || ''}`.trim();
}

function getInitials(user) {
	const first = user.firstName ? user.firstName[0].toUpperCase() : '';
	const last = user.lastName ? user.lastName[0].toUpperCase() : '';
	return `${first}${last}`;
}

function getPrimaryRole(user) {
	return user.roles && user.roles.length > 0 ? user.roles[0] : '';
}

function getRoleBadgeClass(role) {
	switch(role) {
		case 'Admin': return 'bg-danger';
		case 'Guia': return 'bg-primary';
		case 'Padre': return 'bg-success';
		default: return 'badge-secondary';
	}
}

function getDisplayRole(role) {
	const roleDisplayNames = {
		'Admin': 'Administrador',
		'Guia': 'Guía Principal',
		'Padre': 'Padre'
	};
	return roleDisplayNames[role] || role;
}

function getStatusBadgeClass(isActive) {
	return isActive ? 'bg-success' : 'bg-secondary';
}

function getStatusText(isActive) {
	return isActive ? 'Activo' : 'Inactivo';
}

function getAmbienteBadgeClass(ambiente) {
	switch(ambiente) {
		case 'Nido': return 'bg-info';
		case 'Comunidad': return 'bg-success';
		case 'C. Niños': return 'bg-warning';
		case 'Taller I': return 'bg-indigo';
		case 'Taller II': return 'bg-dark';
		default: return 'bg-secondary';
	}
}

function getLastLoginText(lastLoginDate) {
	if (!lastLoginDate) return 'Nunca';
	
	const date = new Date(lastLoginDate);
	const now = new Date();
	const diffMs = now - date;
	const diffMinutes = Math.floor(diffMs / (1000 * 60));
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	
	if (diffMinutes < 60) {
		return `Hace ${diffMinutes} min`;
	} else if (diffHours < 24) {
		return `Hace ${diffHours} horas`;
	} else if (diffDays < 7) {
		return `Hace ${diffDays} días`;
	} else {
		return date.toLocaleDateString('es-ES');
	}
}

// User-specific DataTable Functions
export function initUserGrid(users) {
	destroyDataTable('usersTable');
	// Prepare data for DataTable
	const tableData = users.map(user => [
		// Actions column (first)
		generateUserActions(user.id),
		generateUserColumn(user),
		generateRoleColumn(user),
		generateAmbienteColumn(user),
		generateStatusColumn(user),
		getLastLoginText(user.lastLoginDate),
		user.childrenCount > 0 ? user.childrenCount : '-',
		user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : '-'
	]);

	const options = {
		data: tableData,
		columns: [
			{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '120px' },
			{ title: 'Usuario', width: '25%' },
			{ title: 'Rol', width: '15%' },
			{ title: 'Ambiente', width: '20%' },
			{ title: 'Estado', className: 'text-center', width: '10%' },
			{ title: 'Último Acceso', width: '15%' },
			{ title: 'Hijos', className: 'text-center', width: '8%' },
			{ title: 'Fecha Registro', width: '12%' }
		],
		exportTitle: 'Usuarios del Sistema - Montessori Nova School',
		order: [[1, 'asc']], // Order by name
		buttons: [  // Sobrescribe buttons para personalización
			{
				extend: 'collection',
				className: 'btn btn-flat-primary btn-labeled btn-labeled-start dropdown-toggle me-3 rounded',
				text: '<span class="btn-labeled-icon bg-primary text-white rounded-start"><i class="ph-export"></i></span>Exportar',
				buttons: [
					{
						extend: 'excel',
						text: '<i class="ph-file-xls me-1"></i>Excel',
						className: 'dropdown-item',
						title: 'Usuarios del Sistema - Montessori Nova',
						exportOptions: {
							columns: [1, 2, 3, 4, 5, 6, 7],  // Índices específicos para usuarios (excluye columna 0 de acciones)
							format: {
								body: function (inner, coldex, rowdex) {
									// Manejar casos nulos, undefined o texto plano para evitar errores de jQuery
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									// Para otras columnas, elimina HTML como antes
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					},
					{
						extend: 'copy',
						text: '<i class="ph-clipboard-text me-1"></i>Copiar',
						className: 'dropdown-item',
						exportOptions: {
							columns: [1, 2, 3, 4, 5, 6, 7],  // Índices específicos para usuarios (excluye columna 0 de acciones)
							format: {
								body: function (inner, coldex, rowdex) {
									// Manejar casos nulos, undefined o texto plano para evitar errores de jQuery
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									// Para otras columnas, elimina HTML como antes
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					}
				]
			}
		]
	};

	return initGenericDataTable('usersTable', options);
}

function generateUserActions(userId) {
	return `
		<div class="d-inline-flex">
			<a href="javascript:void(0)" class="text-body me-2" onclick="window.sfBlazorSB.users.viewUser(${userId})" title="Ver perfil">
				<i class="ph-eye"></i>
			</a>
			<a href="javascript:void(0)" class="text-body me-2" onclick="window.sfBlazorSB.users.editUser(${userId})" title="Editar usuario">
				<i class="ph-pencil"></i>
			</a>
			<a href="javascript:void(0)" class="text-body" onclick="window.sfBlazorSB.users.deleteUser(${userId})" title="Eliminar usuario">
				<i class="ph-trash"></i>
			</a>
		</div>
	`;
}

function generateUserColumn(user) {
    const fullName = getFullName(user);
    const initials = getInitials(user);
    const exportText = [initials, fullName, user.email, user.phoneNumber].filter(Boolean).join(' - ');
    
    const profileImage = user.profileImageUrl 
        ? `<img src="${user.profileImageUrl}" class="rounded-circle" width="40" height="40" alt="${fullName}">`
        : `<div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width: 40px; height: 40px; font-weight: 600;">${initials}</div>`;
    
    const phone = user.phoneNumber ? `<div class="text-muted fs-sm"><i class="ph-phone ph-xs me-1"></i>${user.phoneNumber}</div>` : '';
    
    return `
        <div class="d-flex align-items-center" data-export="${exportText.replace(/"/g, '&quot;')}">
            <div class="me-3">${profileImage}</div>
            <div>
                <div class="fw-semibold">${fullName}</div>
                <div class="text-muted fs-sm">${user.email}</div>
                ${phone}
            </div>
        </div>
    `;
}

function generateRoleColumn(user) {
	const primaryRole = getPrimaryRole(user);
	const displayRole = getDisplayRole(primaryRole);
	const roleBadgeClass = getRoleBadgeClass(primaryRole);

	let extraInfo = '';
	if (primaryRole === 'Padre' && user.children && user.children.length > 0) {
		extraInfo = `<div class="text-muted fs-sm mt-1"><i class="ph-baby ph-xs me-1"></i>${user.childrenCount} hijos</div>`;
	} else if (primaryRole === 'Guia') {
		extraInfo = `<div class="text-muted fs-sm mt-1"><i class="ph-user-square ph-xs me-1"></i>${user.metrics?.ninosAsignados || 0} niños</div>`;
	}

	const exportText = displayRole + (extraInfo ? ' ' + extraInfo.replace(/<[^>]*>/g, '').trim() : '');

	return `
		<span class="badge ${roleBadgeClass}" data-export="${exportText}">${displayRole}</span>
		${extraInfo}
	`;
}

function generateAmbienteColumn(user) {
    if (!user.ambientes || user.ambientes.length === 0) {
        return '<span class="text-muted" data-export="-">-</span>';
    }
    
    const ambienteNames = user.ambientes.map(a => a.nombre).join(', ');
    const badges = user.ambientes.map(ambiente => 
        `<span class="badge ${getAmbienteBadgeClass(ambiente.nombre)} badge-outline me-1">${ambiente.nombre}</span>`
    ).join('');
       
       return `<span data-ambientes="${ambienteNames}" data-export="${ambienteNames}">${badges}</span>`;
   }

function generateStatusColumn(user) {
	const statusBadgeClass = getStatusBadgeClass(user.isActive);
	const statusText = getStatusText(user.isActive);
	return `<span class="badge ${statusBadgeClass}">${statusText}</span>`;
}

// Refresh user grid specifically
export function refreshUserGrid(users) {
	destroyDataTable('usersTable');
	initUserGrid(users);
}

// Set callbacks for user grid
export function setUserCallbacks(dotNetRef) {
	userGridCallbacks = dotNetRef;
}

// User action functions
export function viewUser(userId) {
	if (userGridCallbacks) {
		userGridCallbacks.invokeMethodAsync('ViewUser', userId);
	}
}

export function editUser(userId) {
	if (userGridCallbacks) {
		userGridCallbacks.invokeMethodAsync('EditUser', userId);
	}
}

export function deleteUser(userId) {
	if (userGridCallbacks) {
		userGridCallbacks.invokeMethodAsync('DeleteUser', userId);
	}
}

export function toggleUserStatus(userId) {
	if (userGridCallbacks) {
		userGridCallbacks.invokeMethodAsync('ToggleStatus', userId);
	}
}

// Apply filters function
export function applyUserFilters() {
    const roleFilter = document.getElementById('roleFilter')?.value || '';
    const statusFilter = document.getElementById('statusFilter')?.value || '';
    const ambienteFilter = document.getElementById('ambienteFilter')?.value || '';
    const globalSearch = document.getElementById('globalSearch')?.value || '';

    const table = $('#usersTable').DataTable();

    // Clear previous search functions
    $.fn.dataTable.ext.search.splice(0);

    // Add custom search functions
    if (roleFilter) {
        $.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
            return data[2].includes(roleFilter); // Assuming column 2 is role display text
        });
    }

    if (statusFilter) {
        $.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
            const isActive = statusFilter === 'true';
            return data[4].includes(isActive ? 'Activo' : 'Inactivo'); // Assuming column 4 is status
        });
    }

    if (ambienteFilter) {
        $.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			const row = table.row(dataIndex).node();
			const ambientesCell = $(row).find('td').eq(3);
			const ambientesText = ambientesCell.find('[data-ambientes]').attr('data-ambientes') || '';

			// Dividir por comas y verificar coincidencia exacta
			const ambientesArray = ambientesText.split(',').map(a => a.trim());
			return ambientesArray.includes(ambienteFilter);
        });
    }

    // Apply global search
    table.search(globalSearch).draw();
}

// Clear filters function
export function clearUserFilters() {
    // Reset form elements
    const roleFilter = document.getElementById('roleFilter');
    const statusFilter = document.getElementById('statusFilter');
    const ambienteFilter = document.getElementById('ambienteFilter');
    const globalSearch = document.getElementById('globalSearch');

    if (roleFilter) roleFilter.value = '';
    if (statusFilter) statusFilter.value = '';
    if (ambienteFilter) ambienteFilter.value = '';
    if (globalSearch) globalSearch.value = '';

    const table = $('#usersTable').DataTable();
    $.fn.dataTable.ext.search.splice(0); // Clear custom searches
    table.search('').draw(); // Clear global search
}
